// User-facing message rendered in DriverSelectorCell's dispatch warning dialog
// when needsMissingPreadviseWarning() returns true.
export const MISSING_PREADVISE_WARNING_MESSAGE = 'Warning: Missing Preadvise';

/**
 * Checks whether a driver assignment should trigger a "Missing Preadvise"
 * warning.
 *
 * The backend (orders.py) computes this at the order level:
 *   - EXPORT + Hazmat + specific rail terminals (HOU-BPT/HOU-BCT/BTM-SMT)
 *   - Live load OR loaded leg
 *   - No preadvisedatetime
 * The result lives on `order.warnings` as
 * `{ code: 'missing_preadvise', ... }`. We don't re-implement that rule —
 * we just consume the flag.
 *
 * Like Not Railbilled, the prompt is only meaningful for the leg that
 * actually arrives at the rail terminal — the TERMINATE leg, or the
 * DELIVERY leg when the order has no TERMINATE anywhere. Firing on every
 * leg of a multi-leg dispatch produces spurious prompts.
 *
 * `allOrderLegs` is optional; see notRailbilledCheck.ts for the same shape
 * and rationale.
 */
export function needsMissingPreadviseWarning(params: {
  warnings?: { code: string; message?: string }[];
  dispatchGroupLegs?: { legtype?: string }[];
  allOrderLegs?: { legtype?: string }[];
}): boolean {
  const { warnings, dispatchGroupLegs, allOrderLegs } = params;
  if (!warnings?.some((w) => w.code === 'missing_preadvise')) return false;
  if (!dispatchGroupLegs || dispatchGroupLegs.length === 0) return false;

  const hasLegtype = (legs: { legtype?: string }[], type: string) =>
    legs.some((l) => l.legtype && l.legtype.toUpperCase() === type);

  if (hasLegtype(dispatchGroupLegs, 'TERMINATE')) return true;

  const fullLegList = allOrderLegs ?? dispatchGroupLegs;
  if (hasLegtype(fullLegList, 'TERMINATE')) return false;
  return hasLegtype(dispatchGroupLegs, 'DELIVERY');
}
